import { useDispatch, useSelector } from "react-redux";
import { useSocket } from "./useSocket";
import { useCallback, useEffect, useRef, useState } from "react";
import { THIRDPARTY_INTEGRATION_UPDATE, THIRDPARTY_TOKEN_STATUS } from "../constants/socket";
import {
  disconnectIntegration,
  fetchThirdPartyAccount,
  getCompanyInfo,
  getConnectedAccounts,
} from "../store/actions/companyActions";
import { CRM_AUTH_PATHS, MESSAGE_DISPLAY_TIME } from "../constants/thirdParty";

// ---------------------------
// Popup Utils
// ---------------------------
const openPopup = ({
  CRMOauthWindow,
  profilePermissionInfo,
  socketEndpoint,
  authPath,
  companyId,
  userId,
  setIsPopupOpen,
  popupClosedBySuccess,
  setShowFailureMessage,
}) => {
  popupClosedBySuccess.current = false;

  if (CRMOauthWindow?.current && !CRMOauthWindow?.current?.closed) {
    CRMOauthWindow.current.focus();
    return;
  }

  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;
  const width = Math.floor(screenWidth * 0.75);
  const height = Math.floor(screenHeight * 0.75);
  const left = Math.floor((screenWidth - width) / 2);
  const top = Math.floor((screenHeight - height) / 2);
  const params = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`;

  const admin = ["Admin", "Super Admin"].includes(profilePermissionInfo?.name);

  CRMOauthWindow.current = window.open(
    `${socketEndpoint}${authPath}/${companyId}/${userId}/${admin}`,
    "CRMOauthWindow",
    params
  );

  setShowFailureMessage(false);
  setIsPopupOpen(true);

  const timer = setInterval(() => {
    if (!CRMOauthWindow.current || CRMOauthWindow.current.closed) {
      clearInterval(timer);
      setIsPopupOpen(false);
      if (!popupClosedBySuccess.current) setShowFailureMessage(true);
    }
  }, 1000);
};

const closePopup = (CRMOauthWindow, popupClosedBySuccess, success = false) => {
  if (success) popupClosedBySuccess.current = true;
  if (CRMOauthWindow.current) CRMOauthWindow.current.close();
};

// ---------------------------
// WebSocket Handler
// ---------------------------
const handleSocketMessage = ({
  message,
  userId,
  companyId,
  providerName,
  dispatch,
  webSocket,
  CRMOauthWindow,
  popupClosedBySuccess,
  profilePermissionInfo,
  socketEndpoint,
  authPath,
  setShowFailureMessage,
  setShowSuccessMessage,
  setUserAlreadyLinked,
  setIsProcessing,
  setIsPopupOpen,
}) => {
  const response = JSON.parse(message?.data);

  if (
    response.userId !== userId ||
    response.companyId !== companyId ||
    response.app !== providerName ||
    response.type !== THIRDPARTY_TOKEN_STATUS
  )
    return;

  switch (response.status) {
    case 200:
      setShowFailureMessage(false);
      setShowSuccessMessage(true);
      closePopup(CRMOauthWindow, popupClosedBySuccess, true);
      setIsProcessing(false);
      dispatch(fetchThirdPartyAccount(companyId));
      dispatch(getCompanyInfo(companyId));
      dispatch(getConnectedAccounts());
      webSocket?.send(JSON.stringify({ type: THIRDPARTY_INTEGRATION_UPDATE, companyId }));
      break;

    case 403:
      setUserAlreadyLinked({ orgMismatch: response?.orgMismatch });
      setShowSuccessMessage(false);
      setShowFailureMessage(false);
      closePopup(CRMOauthWindow, popupClosedBySuccess, true);
      setIsProcessing(false);
      break;

    case 401:
      setShowSuccessMessage(false);
      setShowFailureMessage(true);
      closePopup(CRMOauthWindow, popupClosedBySuccess);
      setIsProcessing(false);
      break;

    default:
      setShowSuccessMessage(false);
      setShowFailureMessage(false);
      setIsProcessing(false);
      openPopup({
        CRMOauthWindow,
        profilePermissionInfo,
        socketEndpoint,
        authPath,
        companyId,
        userId,
        setIsPopupOpen,
        popupClosedBySuccess,
        setShowFailureMessage,
      });
      break;
  }
};

// ---------------------------
// Main Hook
// ---------------------------
export const useCRMAuth = ({ providerName }) => {
  const webSocket = useSocket();
  const dispatch = useDispatch();

  const companyId = useSelector(state => state?.auth?.user?.company?.id);
  const userId = useSelector(state => state?.auth?.uid);
  const profilePermissionInfo = useSelector(state => state?.company?.profilePermissionInfo);

  const socketEndpoint = process.env.SOCKET_URL;
  const authPath = CRM_AUTH_PATHS[providerName];

  const CRMOauthWindow = useRef(null);
  const popupClosedBySuccess = useRef(false);

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showFailureMessage, setShowFailureMessage] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [userAlreadyLinked, setUserAlreadyLinked] = useState(false);

  const onMessage = useCallback(
    message =>
      handleSocketMessage({
        message,
        userId,
        companyId,
        providerName,
        dispatch,
        webSocket,
        CRMOauthWindow,
        popupClosedBySuccess,
        profilePermissionInfo,
        socketEndpoint,
        authPath,
        setShowFailureMessage,
        setShowSuccessMessage,
        setUserAlreadyLinked,
        setIsProcessing,
        setIsPopupOpen,
      }),
    [companyId, userId, providerName, profilePermissionInfo, socketEndpoint, authPath]
  );

  // Listen to WebSocket
  useEffect(() => {
    if (webSocket?.readyState === 1) {
      webSocket.addEventListener("message", onMessage);
      return () => webSocket.removeEventListener("message", onMessage);
    }
  }, [webSocket, onMessage]);

  // Auto-clear messages
  useEffect(() => {
    let timer;
    if (showSuccessMessage || showFailureMessage) {
      timer = setTimeout(() => {
        setShowSuccessMessage(false);
        setShowFailureMessage(false);
      }, MESSAGE_DISPLAY_TIME);
      return () => clearTimeout(timer);
    }
  }, [showSuccessMessage, showFailureMessage]);

  useEffect(() => {
    let timer;
    if (userAlreadyLinked) {
      timer = setTimeout(() => {
        setUserAlreadyLinked(false);
      }, MESSAGE_DISPLAY_TIME);
    }
    return () => clearTimeout(timer);
  }, [userAlreadyLinked]);

  const checkTokenStatus = () => {
    setShowFailureMessage(false);
    setIsProcessing(true);
    if (webSocket?.readyState === 1) {
      webSocket.send(JSON.stringify({ type: THIRDPARTY_TOKEN_STATUS, companyId, userId, app: providerName }));
    }
  };

  const disconnectAccount = async provider => {
    setIsProcessing(true);
    const res = await dispatch(disconnectIntegration(companyId, provider));
    if (res?.status === 200) {
      dispatch(getCompanyInfo(companyId));
      setIsProcessing(false);
      webSocket?.send(JSON.stringify({ type: THIRDPARTY_INTEGRATION_UPDATE, companyId }));
    }
  };

  const openAuthPopup = () =>
    openPopup({
      CRMOauthWindow,
      profilePermissionInfo,
      socketEndpoint,
      authPath,
      companyId,
      userId,
      setIsPopupOpen,
      popupClosedBySuccess,
      setShowFailureMessage,
    });

  const closeAuthPopup = success => closePopup(CRMOauthWindow, popupClosedBySuccess, success);

  return {
    isProcessing,
    isPopupOpen,
    showSuccessMessage,
    showFailureMessage,
    userAlreadyLinked,
    setUserAlreadyLinked,
    checkTokenStatus,
    disconnectAccount,
    openAuthPopup,
    closeAuthPopup,
  };
};
